/**
 * SignalScreener — "find tickers with N+ analog signals across the 5 readout windows."
 *
 * Filters GET /api/signals/screener by a minimum number of QUALIFYING windows (out of
 * TICKER_SIGNAL_WINDOWS = 10/20/65/130/260), a minimum match-quality floor, an optional
 * direction, a minimum analog sample size, a minimum average move (read off each window's
 * OWN called direction — avg_up/avg_dn are both positive magnitudes, so this is one
 * unified filter rather than two), and whether to exclude delisted-heavy windows. A window
 * only counts as a "signal" if it has a clear up/down call — match quality alone isn't
 * enough, even when direction is "any" (see stocks_api resources/signal_screener.py /
 * .claude/rules/signal-screener.md).
 *
 * Industry, market cap tier and momentum rank are CLIENT-SIDE filters over the already-
 * fetched batch — the API has no params for them, it just adds market_cap/momentum_rank
 * as plain per-ticker facts alongside industry.
 *
 * Fully public — same as TickerTimeframes' /api/ticker/<t>/signals, no login required.
 * The per-window fields rendered here (match_quality, direction) are already ungated
 * everywhere they appear in the product; this page just filters/aggregates them across
 * the universe instead of scoping to one ticker.
 */
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert, Badge, Button, ButtonGroup, Container, Dropdown, DropdownToggle, DropdownMenu,
} from "reactstrap";
import { FaFilter } from "react-icons/fa";
import { usePostHog } from "@posthog/react";

import requests from "../requests";
import { useSeo } from "../hooks/useSeo";
import { QUALITY_LABEL, type WindowReadout } from "./TickerTimeframes";
import { TableRowSkeleton } from "./SkeletonLoader";
import "../style/signal-screener-styles.css";

// Mirrors TICKER_SIGNAL_WINDOWS (resources/ticker.py) — display windows, short to long.
const WINDOWS = [10, 20, 65, 130, 260] as const;

const MIN_WINDOWS_OPTIONS = [1, 2, 3, 4, 5] as const;
const DEFAULT_MIN_WINDOWS = 3;

// UI labels for the API's own match_quality vocabulary (weak/fair/strong) — display-only,
// the wire format never uses these words. "Loose"/"Close" read better than "weak"/"strong"
// for a filter control; QUALITY_LABEL (imported) still drives the per-chip text so the
// wording matches TickerTimeframes exactly.
const QUALITY_OPTIONS: { value: ScreenerMinQuality; label: string }[] = [
  { value: "weak", label: "Loose+" },
  { value: "fair", label: "Fair+" },
  { value: "strong", label: "Close only" },
];
const DEFAULT_MIN_QUALITY: ScreenerMinQuality = "weak";

const DIRECTION_OPTIONS: { value: ScreenerDirection; label: string }[] = [
  { value: "any", label: "Any" },
  { value: "up", label: "Bullish" },
  { value: "down", label: "Bearish" },
];
const DEFAULT_DIRECTION: ScreenerDirection = "any";

// Server-side window-quality filters — same shape as the three above, sent to the API
// and affecting qualifying_count/qualifying_windows (see resources/signal_screener.py).
const MIN_ANALOGS_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "Any" },
  { value: 10, label: "10+" },
  { value: 25, label: "25+" },
  { value: 50, label: "50+" },
];
const DEFAULT_MIN_ANALOGS = 0;

// avg_up/avg_dn are both stored as positive magnitudes regardless of direction, so the
// backend picks the field matching each window's own called direction — one unified
// "how big was the move" filter rather than two the caller would have to choose between.
const MIN_AVG_MOVE_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "Any" },
  { value: 2, label: "2%+" },
  { value: 5, label: "5%+" },
  { value: 10, label: "10%+" },
];
const DEFAULT_MIN_AVG_MOVE = 0;

const DELISTED_OPTIONS: { value: boolean; label: string }[] = [
  { value: false, label: "All signals" },
  { value: true, label: "Exclude delisted-heavy" },
];
const DEFAULT_EXCLUDE_DELISTED = false;

// Client-side ticker-attribute filters — like Industry, these have no API param; the
// backend just adds market_cap/momentum_rank as plain per-ticker facts and the page
// filters the already-fetched batch, same division of labor as industry filtering.
type MarketCapTier = "any" | "large" | "mid" | "small";
const MARKET_CAP_OPTIONS: { value: MarketCapTier; label: string }[] = [
  { value: "any", label: "Any" },
  { value: "large", label: "Large" },
  { value: "mid", label: "Mid" },
  { value: "small", label: "Small" },
];
const DEFAULT_MARKET_CAP_TIER: MarketCapTier = "any";
// No existing small/mid/large-cap convention exists anywhere in either repo (checked
// before adding this) — these are a new boundary choice, matching common industry usage:
// Large >= $10B, Mid $2B-$10B, Small < $2B.
const LARGE_CAP_MIN = 10_000_000_000;
const MID_CAP_MIN = 2_000_000_000;

function marketCapTierOf(marketCap: number | null): MarketCapTier | null {
  if (marketCap === null) return null;
  if (marketCap >= LARGE_CAP_MIN) return "large";
  if (marketCap >= MID_CAP_MIN) return "mid";
  return "small";
}

const MOMENTUM_RANK_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "Any" },
  { value: 50, label: "Top 50%" },
  { value: 80, label: "Top 20%" },
  { value: 90, label: "Top 10%" },
];
const DEFAULT_MIN_MOMENTUM_RANK = 0;

function parseMinWindows(raw: string | null): number {
  const n = raw ? Number(raw) : NaN;
  return MIN_WINDOWS_OPTIONS.includes(n as (typeof MIN_WINDOWS_OPTIONS)[number])
    ? n
    : DEFAULT_MIN_WINDOWS;
}

function parseMinQuality(raw: string | null): ScreenerMinQuality {
  return raw === "fair" || raw === "strong" ? raw : DEFAULT_MIN_QUALITY;
}

function parseDirection(raw: string | null): ScreenerDirection {
  return raw === "up" || raw === "down" ? raw : DEFAULT_DIRECTION;
}

function parseMinAnalogs(raw: string | null): number {
  const n = raw ? Number(raw) : NaN;
  return MIN_ANALOGS_OPTIONS.some(opt => opt.value === n) ? n : DEFAULT_MIN_ANALOGS;
}

function parseMinAvgMove(raw: string | null): number {
  const n = raw ? Number(raw) : NaN;
  return MIN_AVG_MOVE_OPTIONS.some(opt => opt.value === n) ? n : DEFAULT_MIN_AVG_MOVE;
}

function parseExcludeDelisted(raw: string | null): boolean {
  return raw === "1";
}

function parseMarketCapTier(raw: string | null): MarketCapTier {
  return raw === "large" || raw === "mid" || raw === "small" ? raw : DEFAULT_MARKET_CAP_TIER;
}

function parseMinMomentumRank(raw: string | null): number {
  const n = raw ? Number(raw) : NaN;
  return MOMENTUM_RANK_OPTIONS.some(opt => opt.value === n) ? n : DEFAULT_MIN_MOMENTUM_RANK;
}

function SignalChip({ row }: { row: WindowReadout }): React.JSX.Element {
  const dirClass = row.direction === "up" ? "ss-chip-up" : "ss-chip-down";
  const arrow = row.direction === "up" ? "▲" : "▼";
  return (
    <span className="ss-chip" title={`${row.window_name}: ${QUALITY_LABEL[row.match_quality]}`}>
      <span className="ss-chip-window">{row.window_name}</span>
      <span className={dirClass}>{arrow}</span>
      <span className="ss-chip-quality">{QUALITY_LABEL[row.match_quality]}</span>
    </span>
  );
}

export function SignalScreener(): React.JSX.Element {
  useSeo({
    title: "Signal Screener — Tickers with Multiple Analog Signals",
    description: "Find tickers whose historical analogs agree across several timeframes at once — filter by how many windows have a signal, how close the analog match is, and direction.",
    path: "/screener",
  });
  const posthog = usePostHog();
  const [searchParams, setSearchParams] = useSearchParams();

  const [minWindows, setMinWindows] = useState<number>(() => parseMinWindows(searchParams.get("min_windows")));
  const [minQuality, setMinQuality] = useState<ScreenerMinQuality>(() => parseMinQuality(searchParams.get("min_quality")));
  const [direction, setDirection] = useState<ScreenerDirection>(() => parseDirection(searchParams.get("direction")));
  const [minAnalogs, setMinAnalogs] = useState<number>(() => parseMinAnalogs(searchParams.get("min_analogs")));
  const [minAvgMove, setMinAvgMove] = useState<number>(() => parseMinAvgMove(searchParams.get("min_avg_move")));
  const [excludeDelisted, setExcludeDelisted] = useState<boolean>(
    () => parseExcludeDelisted(searchParams.get("exclude_delisted"))
  );
  // Industry is filtered CLIENT-SIDE over the already-fetched batch (the API has no
  // `industries=` param) — same division of labor as ClassicPatterns.tsx's sector filter.
  const [selectedIndustries, setSelectedIndustries] = useState<Set<string>>(
    () => new Set(searchParams.get("industries")?.split(",").filter(Boolean) ?? [])
  );
  const [industryDropdownOpen, setIndustryDropdownOpen] = useState(false);
  // Market cap tier and momentum rank are client-side too, for the same reason.
  const [marketCapTier, setMarketCapTier] = useState<MarketCapTier>(
    () => parseMarketCapTier(searchParams.get("market_cap_tier"))
  );
  const [minMomentumRank, setMinMomentumRank] = useState<number>(
    () => parseMinMomentumRank(searchParams.get("min_momentum_rank"))
  );

  const [data, setData] = useState<SignalScreenerResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleMinWindows = (value: number): void => {
    setMinWindows(value);
    posthog.capture("signal_screener_filtered", { filter_type: "min_windows", value });
  };
  const handleMinQuality = (value: ScreenerMinQuality): void => {
    setMinQuality(value);
    posthog.capture("signal_screener_filtered", { filter_type: "min_quality", value });
  };
  const handleDirection = (value: ScreenerDirection): void => {
    setDirection(value);
    posthog.capture("signal_screener_filtered", { filter_type: "direction", value });
  };
  const handleMinAnalogs = (value: number): void => {
    setMinAnalogs(value);
    posthog.capture("signal_screener_filtered", { filter_type: "min_analogs", value });
  };
  const handleMinAvgMove = (value: number): void => {
    setMinAvgMove(value);
    posthog.capture("signal_screener_filtered", { filter_type: "min_avg_move", value });
  };
  const handleExcludeDelisted = (value: boolean): void => {
    setExcludeDelisted(value);
    posthog.capture("signal_screener_filtered", { filter_type: "exclude_delisted", value });
  };
  const handleMarketCapTier = (value: MarketCapTier): void => {
    setMarketCapTier(value);
    posthog.capture("signal_screener_filtered", { filter_type: "market_cap_tier", value });
  };
  const handleMinMomentumRank = (value: number): void => {
    setMinMomentumRank(value);
    posthog.capture("signal_screener_filtered", { filter_type: "min_momentum_rank", value });
  };
  const toggleIndustry = (industry: string): void => {
    setSelectedIndustries(prev => {
      const next = new Set(prev);
      if (next.has(industry)) {
        next.delete(industry);
      } else {
        next.add(industry);
      }
      return next;
    });
    posthog.capture("signal_screener_filtered", { filter_type: "industry", value: industry });
  };

  // Sync URL params — omit at the default so a bare /screener URL stays unchanged.
  useEffect(() => {
    const params = new URLSearchParams();
    if (minWindows !== DEFAULT_MIN_WINDOWS) params.set("min_windows", minWindows.toString());
    if (minQuality !== DEFAULT_MIN_QUALITY) params.set("min_quality", minQuality);
    if (direction !== DEFAULT_DIRECTION) params.set("direction", direction);
    if (minAnalogs !== DEFAULT_MIN_ANALOGS) params.set("min_analogs", minAnalogs.toString());
    if (minAvgMove !== DEFAULT_MIN_AVG_MOVE) params.set("min_avg_move", minAvgMove.toString());
    if (excludeDelisted !== DEFAULT_EXCLUDE_DELISTED) params.set("exclude_delisted", "1");
    if (selectedIndustries.size > 0) {
      params.set("industries", Array.from(selectedIndustries).join(","));
    }
    if (marketCapTier !== DEFAULT_MARKET_CAP_TIER) params.set("market_cap_tier", marketCapTier);
    if (minMomentumRank !== DEFAULT_MIN_MOMENTUM_RANK) {
      params.set("min_momentum_rank", minMomentumRank.toString());
    }
    setSearchParams(params, { replace: true });
  }, [
    minWindows, minQuality, direction, minAnalogs, minAvgMove, excludeDelisted,
    selectedIndustries, marketCapTier, minMomentumRank, setSearchParams,
  ]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      min_windows: minWindows.toString(),
      min_quality: minQuality,
      direction,
      min_analogs: minAnalogs.toString(),
      min_avg_move: minAvgMove.toString(),
      exclude_delisted: excludeDelisted.toString(),
    });
    requests.get(`/api/signals/screener?${params.toString()}`)
      .then(res => {
        if (!alive) return;
        setData(res.data as SignalScreenerResponse);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setData(null);
        setError("Could not load the signal screener right now.");
        setLoading(false);
      });
    return (): void => { alive = false; };
  }, [minWindows, minQuality, direction, minAnalogs, minAvgMove, excludeDelisted]);

  useEffect(() => {
    posthog.capture("signal_screener_viewed");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tickers = data?.tickers ?? [];
  const industryOf = (t: SignalScreenerTicker): string => t.industry || "Other";
  // Industries actually present in the fetched batch, for the filter dropdown — derived
  // rather than a hardcoded list so it always matches what's on screen. "Other"
  // (no fundamentals industry — crypto/futures/forex, or a coverage gap) sorts last.
  const availableIndustries = Array.from(new Set(tickers.map(industryOf))).sort((a, b) =>
    a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b)
  );
  const hasClientFilter =
    selectedIndustries.size > 0 ||
    marketCapTier !== DEFAULT_MARKET_CAP_TIER ||
    minMomentumRank !== DEFAULT_MIN_MOMENTUM_RANK;
  const filteredTickers = tickers.filter(t => {
    if (selectedIndustries.size > 0 && !selectedIndustries.has(industryOf(t))) return false;
    if (marketCapTier !== "any" && marketCapTierOf(t.market_cap) !== marketCapTier) return false;
    if (minMomentumRank > 0 && (t.momentum_rank === null || t.momentum_rank < minMomentumRank)) {
      return false;
    }
    return true;
  });

  return (
    <Container className="signal-screener">
      <div className="page-header">
        <h1 className="page-title">Signal Screener</h1>
        {data && (
          <Badge color="primary">
            {data.count_total_matches} ticker{data.count_total_matches === 1 ? "" : "s"} match
            {data.date && ` · data as of ${data.date}`}
          </Badge>
        )}
      </div>
      <p className="ss-lede">
        Find tickers whose historical analogs agree across several timeframes at once —
        set how many of the 5 windows need a signal, how close the match has to be, and
        which direction.
      </p>

      <div className="ss-filters">
        <div className="ss-filter-group">
          <span className="ss-filter-label">Min. windows</span>
          <ButtonGroup size="sm">
            {MIN_WINDOWS_OPTIONS.map(n => (
              <Button
                key={n}
                color={minWindows === n ? "primary" : "secondary"}
                outline={minWindows !== n}
                onClick={() => handleMinWindows(n)}
              >
                {n} of {WINDOWS.length}
              </Button>
            ))}
          </ButtonGroup>
        </div>

        <div className="ss-filter-group">
          <span className="ss-filter-label">Match quality</span>
          <ButtonGroup size="sm">
            {QUALITY_OPTIONS.map(opt => (
              <Button
                key={opt.value}
                color={minQuality === opt.value ? "primary" : "secondary"}
                outline={minQuality !== opt.value}
                onClick={() => handleMinQuality(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </ButtonGroup>
        </div>

        <div className="ss-filter-group">
          <span className="ss-filter-label">Direction</span>
          <ButtonGroup size="sm">
            {DIRECTION_OPTIONS.map(opt => (
              <Button
                key={opt.value}
                color={direction === opt.value ? "primary" : "secondary"}
                outline={direction !== opt.value}
                onClick={() => handleDirection(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </ButtonGroup>
        </div>

        <div className="ss-filter-group">
          <span className="ss-filter-label">Min. analogs</span>
          <ButtonGroup size="sm">
            {MIN_ANALOGS_OPTIONS.map(opt => (
              <Button
                key={opt.value}
                color={minAnalogs === opt.value ? "primary" : "secondary"}
                outline={minAnalogs !== opt.value}
                onClick={() => handleMinAnalogs(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </ButtonGroup>
        </div>

        <div className="ss-filter-group">
          <span className="ss-filter-label">Min. avg move</span>
          <ButtonGroup size="sm">
            {MIN_AVG_MOVE_OPTIONS.map(opt => (
              <Button
                key={opt.value}
                color={minAvgMove === opt.value ? "primary" : "secondary"}
                outline={minAvgMove !== opt.value}
                onClick={() => handleMinAvgMove(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </ButtonGroup>
        </div>

        <div className="ss-filter-group">
          <span className="ss-filter-label">Delisted</span>
          <ButtonGroup size="sm">
            {DELISTED_OPTIONS.map(opt => (
              <Button
                key={String(opt.value)}
                color={excludeDelisted === opt.value ? "primary" : "secondary"}
                outline={excludeDelisted !== opt.value}
                onClick={() => handleExcludeDelisted(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </ButtonGroup>
        </div>

        <div className="ss-filter-group">
          <span className="ss-filter-label">Industry</span>
          <Dropdown
            isOpen={industryDropdownOpen}
            toggle={() => setIndustryDropdownOpen(prev => !prev)}
            size="sm"
          >
            <DropdownToggle caret outline color={selectedIndustries.size > 0 ? "primary" : "secondary"}>
              <FaFilter style={{ marginRight: 4 }} />
              {selectedIndustries.size === 0
                ? "All Industries"
                : `${selectedIndustries.size} ${selectedIndustries.size > 1 ? "Industries" : "Industry"}`}
            </DropdownToggle>
            <DropdownMenu className="ss-industry-dropdown">
              {availableIndustries.map(industry => (
                <label key={industry} className="ss-industry-option" onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIndustries.has(industry)}
                    onChange={() => toggleIndustry(industry)}
                  />
                  <span>{industry}</span>
                </label>
              ))}
              {selectedIndustries.size > 0 && (
                <>
                  <div className="dropdown-divider" />
                  <button
                    className="ss-industry-clear"
                    onClick={e => { e.stopPropagation(); setSelectedIndustries(new Set()); }}
                  >
                    Clear all
                  </button>
                </>
              )}
            </DropdownMenu>
          </Dropdown>
        </div>

        <div className="ss-filter-group">
          <span className="ss-filter-label">Market cap</span>
          <ButtonGroup size="sm">
            {MARKET_CAP_OPTIONS.map(opt => (
              <Button
                key={opt.value}
                color={marketCapTier === opt.value ? "primary" : "secondary"}
                outline={marketCapTier !== opt.value}
                onClick={() => handleMarketCapTier(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </ButtonGroup>
        </div>

        <div className="ss-filter-group">
          <span className="ss-filter-label">Momentum rank</span>
          <ButtonGroup size="sm">
            {MOMENTUM_RANK_OPTIONS.map(opt => (
              <Button
                key={opt.value}
                color={minMomentumRank === opt.value ? "primary" : "secondary"}
                outline={minMomentumRank !== opt.value}
                onClick={() => handleMinMomentumRank(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </ButtonGroup>
        </div>
      </div>

      {loading ? (
        <TableRowSkeleton rows={8} />
      ) : error ? (
        <Alert color="warning">{error}</Alert>
      ) : filteredTickers.length === 0 ? (
        <div className="ss-empty">
          <p>No tickers currently clear that bar.</p>
          <p className="text-muted">
            {hasClientFilter
              ? "Try clearing the industry, market cap, or momentum filters, lowering the minimum windows, or the match-quality floor."
              : "Try lowering the minimum windows or the match-quality floor."}
          </p>
        </div>
      ) : (
        <>
          <p className="ss-count-note">
            {hasClientFilter
              ? <>Showing {filteredTickers.length} of {data?.count_returned} fetched tickers matching the selected filters</>
              : <>Showing {data?.count_returned} of {data?.count_total_matches} matching tickers</>}
          </p>
          <div className="ss-tscroll">
            <table className="ss-table">
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>Industry</th>
                  <th>Qualifying</th>
                  <th>Signals</th>
                </tr>
              </thead>
              <tbody>
                {filteredTickers.map(t => (
                  <tr key={t.ticker}>
                    <td>
                      <Link className="ss-ticker-cell" to={`/ticker/${encodeURIComponent(t.ticker)}`}>
                        {t.ticker}
                      </Link>
                      {t.ticker_name && t.ticker_name !== t.ticker && (
                        <span className="ss-ticker-name">{t.ticker_name}</span>
                      )}
                    </td>
                    <td className="ss-industry-cell">{industryOf(t)}</td>
                    <td>
                      <span className="ss-qualifying-badge">
                        {t.qualifying_count} of {WINDOWS.length}
                      </span>
                    </td>
                    <td>
                      <div className="ss-signals">
                        {t.qualifying_windows.map(wk => {
                          const row = t.windows[String(wk)];
                          return row ? <SignalChip key={wk} row={row} /> : null;
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Container>
  );
}
